import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, formatINR, toInputDate } from '../utils/api';

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    api.get('/clients').then(d => { setClients(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = clients.filter(c =>
    !search || c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.email || '').toLowerCase().includes(search.toLowerCase())
  );

  const totalValue       = clients.reduce((s,c) => s + (c.total_value||0), 0);
  const totalReceived    = clients.reduce((s,c) => s + (c.total_received||0), 0);
  const totalOutstanding = clients.reduce((s,c) => s + (c.total_outstanding||0), 0);

  return (
    <div className="page-content">
      <div className="topbar">
        <div className="topbar-title">Clients</div>
        <div className="topbar-right">
          <input style={{padding:'5px 10px',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)',fontSize:12,width:180,fontFamily:'inherit'}}
            placeholder="Search clients…" value={search} onChange={e => setSearch(e.target.value)} />
          <button className="btn btn-primary" onClick={() => setShowAdd(true)}>+ Add Client</button>
        </div>
      </div>

      <div className="kpi-grid kpi-grid-4">
        <div className="kpi-card"><div className="kpi-label">Total Clients</div><div className="kpi-value">{clients.length}</div></div>
        <div className="kpi-card accent"><div className="kpi-label">Total Contract Value</div><div className="kpi-value red">{formatINR(totalValue)}</div></div>
        <div className="kpi-card"><div className="kpi-label">Total Received</div><div className="kpi-value green">{formatINR(totalReceived)}</div></div>
        <div className="kpi-card"><div className="kpi-label">Total Outstanding</div><div className="kpi-value danger">{formatINR(totalOutstanding)}</div></div>
      </div>

      <div className="card">
        <div className="card-header"><span className="card-title">All Clients</span></div>
        {loading ? <div className="loading-state">Loading…</div> : filtered.length === 0 ? (
          <div className="empty-state"><div className="empty-state-icon">👥</div>{clients.length === 0 ? 'No clients yet. Add one above.' : 'No results.'}</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr>
                <th>Client</th><th>GSTIN</th><th>Phone</th>
                <th className="num">Projects</th>
                <th className="num">Contract Value</th>
                <th className="num">Received</th>
                <th className="num">Outstanding</th>
              </tr></thead>
              <tbody>
                {filtered.map(c => (
                  <tr key={c.id} onClick={() => navigate(`/clients/${c.id}`)}>
                    <td>
                      <div style={{fontWeight:600,color:'var(--text-dark)'}}>{c.name}</div>
                      {c.email && <div style={{fontSize:10,color:'var(--text-light)'}}>{c.email}</div>}
                    </td>
                    <td style={{fontSize:11,color:'var(--text-light)'}}>{c.gstin || '—'}</td>
                    <td style={{fontSize:11,color:'var(--text-light)'}}>{c.phone || '—'}</td>
                    <td className="num">{c.project_count}</td>
                    <td className="num">{formatINR(c.total_value)}</td>
                    <td className="num" style={{color:'var(--success)',fontWeight:600}}>{formatINR(c.total_received)}</td>
                    <td className="num" style={{color:c.total_outstanding>0?'var(--danger)':'var(--success)',fontWeight:700}}>{formatINR(c.total_outstanding)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAdd && <ClientFormModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}

export function ClientFormModal({ client, onClose, onSaved }) {
  const isEdit = !!client;
  const [form, setForm] = useState({
    name: client?.name||'', gstin: client?.gstin||'', pan: client?.pan||'',
    phone: client?.phone||'', email: client?.email||'', website: client?.website||'',
    address: client?.address||'', city: client?.city||'', state: client?.state||'',
    pincode: client?.pincode||'', notes: client?.notes||'', link: client?.link||'',
    paymentAdvance: client?.payment_advance||50, paymentInterim: client?.payment_interim||25, paymentFinal: client?.payment_final||25,
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');
  function setF(k,v) { setForm(f=>({...f,[k]:v})); }

  async function handleSave() {
    if (!form.name) { setError('Client name is required.'); return; }
    const total = +form.paymentAdvance + +form.paymentInterim + +form.paymentFinal;
    if (total !== 100) { setError(`Payment terms must sum to 100% (currently ${total}%).`); return; }
    setSaving(true); setError('');
    try {
      if (isEdit) await api.put(`/clients/${client.id}`, form);
      else await api.post('/clients', form);
      onSaved();
    } catch(err) { setError(err.message); setSaving(false); }
  }

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{maxWidth:540}}>
        <div className="modal-header"><div className="modal-title">{isEdit?'Edit Client':'Add Client'}</div></div>
        <div className="modal-body">
          {error && <div className="setup-error">{error}</div>}
          <div className="form-group"><label className="form-label">Client Name *</label>
            <input className="form-input" value={form.name} onChange={e=>setF('name',e.target.value)} autoFocus={!isEdit} /></div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">GSTIN</label><input className="form-input" value={form.gstin} onChange={e=>setF('gstin',e.target.value)} /></div>
            <div className="form-group"><label className="form-label">PAN</label><input className="form-input" value={form.pan} onChange={e=>setF('pan',e.target.value)} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Phone</label><input className="form-input" value={form.phone} onChange={e=>setF('phone',e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Email</label><input className="form-input" type="email" value={form.email} onChange={e=>setF('email',e.target.value)} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Website</label><input className="form-input" value={form.website} onChange={e=>setF('website',e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Drive Link</label><input className="form-input" value={form.link} onChange={e=>setF('link',e.target.value)} /></div>
          </div>
          <div className="form-group"><label className="form-label">Address</label>
            <input className="form-input" value={form.address} onChange={e=>setF('address',e.target.value)} /></div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">City</label><input className="form-input" value={form.city} onChange={e=>setF('city',e.target.value)} /></div>
            <div className="form-group"><label className="form-label">State</label><input className="form-input" value={form.state} onChange={e=>setF('state',e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Pincode</label><input className="form-input" value={form.pincode} onChange={e=>setF('pincode',e.target.value)} /></div>
          </div>
          <div className="form-group"><label className="form-label">Payment Terms (Advance / Interim / Final %)</label>
            <div className="form-row">
              <input className="form-input" type="number" value={form.paymentAdvance} onChange={e=>setF('paymentAdvance',+e.target.value)} />
              <input className="form-input" type="number" value={form.paymentInterim} onChange={e=>setF('paymentInterim',+e.target.value)} />
              <input className="form-input" type="number" value={form.paymentFinal}   onChange={e=>setF('paymentFinal',  +e.target.value)} />
            </div>
          </div>
          <div className="form-group"><label className="form-label">Notes</label>
            <textarea className="form-textarea" value={form.notes} onChange={e=>setF('notes',e.target.value)} /></div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving?'Saving…':isEdit?'Save Changes':'Add Client'}</button>
        </div>
      </div>
    </div>
  );
}
